import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Drawer, Spin, Select, DatePicker, Tooltip, Modal, message, Button } from 'antd';
import { ExportOutlined, RedoOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { FilterTitle, FilterTag } from 'components/common';
import {
	getClientContractListV2,
	searchClientFiltersV2,
	updateClientContractV2,
	exportClientContractCSV,
} from 'store/clientSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { searchVehicleTypesV2 } from 'store/vehicleSliceV2';
import { searchRouteFiltersV2 } from 'store/routeSliceV2';
import { usePersistedState } from 'hooks';
import {
	getTableColumns,
	StatusOptions,
	ApprovalStatusOptions,
} from './ClientContractListPageV2.config';

const { Search } = Input;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

// Fields shown for the contract that is blocking activation (collision modal).
const CONFLICT_FIELDS = [
	{ key: 'client_name', label: 'Client' },
	{ key: 'project_code', label: 'Project Code' },
	{ key: 'project_name', label: 'Project' },
	{ key: 'vehicle_type_name', label: 'Vehicle Type' },
	{ key: 'route_name', label: 'Route' },
	{ key: 'route_code', label: 'Route Code' },
	{ key: 'contract_date', label: 'Contract Date' },
	{ key: 'expiry_date', label: 'Expiry Date' },
	{ key: 'billing_type', label: 'Billing Type' },
];

export default function ClientContractListPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('clientContractList:search', '');
	const [loading, setLoading] = useState(false);
	const [statusUpdateLoading, setStatusUpdateLoading] = useState({});
	const [contractsData, setContractsData] = useState([]);
	const [pagination, setPagination] = usePersistedState('clientContractList:pagination', {
		current: 1,
		pageSize: 20,
		total: 0,
	});
	const [filters, setFilters] = usePersistedState('clientContractList:filters', {});
	const [dateRange, setDateRange] = useState(null);

	// Filter Options State
	const [filterOptions, setFilterOptions] = useState({
		cities: [],
		clients: [],
		projects: [],
		businessUnits: [],
		vehicleTypes: [],
		routes: [],
	});

	// Helper function to extract filter options from API response
	const extractFilterOptions = (filterList) => {
		const newFilterOptions = {};

		if (filterList.cities) {
			newFilterOptions.cities = filterList.cities.map((city) => ({
				value: city,
				label: city,
			}));
		}

		if (filterList.clients) {
			newFilterOptions.clients = filterList.clients.map((client) => ({
				value: client.id,
				label: client.name,
			}));
		}

		if (filterList.projects) {
			newFilterOptions.projects = filterList.projects.map((project) => ({
				value: project.id,
				label: project.code || project.name,
			}));
		}

		if (filterList.business_units) {
			newFilterOptions.businessUnits = filterList.business_units.map((unit) => ({
				value: unit.id,
				label: unit.name,
			}));
		}

		if (filterList.vehicle_type) {
			newFilterOptions.vehicleTypes = filterList.vehicle_type.map((type) => ({
				value: type.id ?? type.vehicle_type_id ?? type.value,
				label: type.vehicle_type_name ?? type.name ?? type.label,
			}));
		}

		if (filterList.routes) {
			newFilterOptions.routes = filterList.routes.map((route) => ({
				value: route.id ?? route.route_id ?? route.value,
				label: route.route_name ?? route.name ?? route.label,
			}));
		}

		return newFilterOptions;
	};

	// Fetch filter options (clients, projects, vehicle types) when drawer opens
	const fetchFilterOptions = async () => {
		try {
			// Fetch clients
			const clientResponse = await dispatch(searchClientFiltersV2({ limit: 1000 }));
			if (clientResponse?.results) {
				setFilterOptions((prev) => ({
					...prev,
					clients: clientResponse.results.map((client) => ({
						value: client.value,
						label: client.label,
					})),
				}));
			}

			// Fetch projects
			const projectResponse = await dispatch(
				getProjectListV2({
					filter_by: {},
					pagination: { page: 1, per_page: 1000 },
				})
			);
			if (projectResponse?.rows) {
				setFilterOptions((prev) => ({
					...prev,
					projects: projectResponse.rows.map((project) => ({
						value: project.id,
						label: project.project_code || project.project_name,
					})),
				}));
			}

			const vehicleTypeResponse = await dispatch(
				searchVehicleTypesV2({
					filter_by: { is_active: true },
					pagination: { page: 1, per_page: 100 },
				})
			);
			if (vehicleTypeResponse?.rows) {
				setFilterOptions((prev) => ({
					...prev,
					vehicleTypes: vehicleTypeResponse.rows.map((type) => ({
						value: type.id,
						label: type.vehicle_type_name,
					})),
				}));
			}

			// Fetch routes
			const routeResponse = await dispatch(
				searchRouteFiltersV2({ filter_by: {}, limit: 1000 })
			);
			if (routeResponse?.results) {
				setFilterOptions((prev) => ({
					...prev,
					routes: routeResponse.results.map((item) => ({
						value: item.value || item.id,
						label: item.label || item.route_name || '-',
					})),
				}));
			}
		} catch (error) {
			console.error('Error fetching filter options:', error);
		}
	};

	// Fetch filter options when drawer opens
	useEffect(() => {
		if (filterDrawerOpen) {
			fetchFilterOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen]);

	// Fetch client contracts on mount and when pagination/filters change
	useEffect(() => {
		fetchClientContracts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current, pagination.pageSize, filters]);

	const fetchClientContracts = async () => {
		setLoading(true);
		try {
			const response = await dispatch(
				getClientContractListV2({
					filter_by: filters,
					pagination: {
						page: pagination.current,
						per_page: pagination.pageSize,
					},
				})
			);

			if (response) {
				setContractsData(response.rows || []);
				setPagination((prev) => ({
					...prev,
					total: response.count || 0,
				}));

				// Extract and set filter options if available in response
				if (response.filter_list) {
					const newFilterOptions = extractFilterOptions(response.filter_list);
					setFilterOptions((prev) => ({
						...prev,
						...newFilterOptions,
					}));
				}
			}
		} catch (error) {
			console.error('Error fetching client contracts:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchValue(value);
		setFilters((prev) => {
			const newFilters = { ...prev };
			if (value) {
				newFilters.query_str = value;
			} else {
				delete newFilters.query_str;
			}
			// Remove old search_term if it exists
			delete newFilters.search_term;
			return newFilters;
		});
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
			total: paginationConfig.total,
		});
	};

	const handleExport = async () => {
		try {
			await dispatch(
				exportClientContractCSV({
					filter_by: filters,
				})
			);
		} catch (error) {
			console.error('Error exporting client contracts:', error);
		}
	};

	const handleSync = () => {
		fetchClientContracts();
	};

	const handleCreate = () => {
		navigate('/client-contracts/add');
	};

	const handleFilterChange = (filterKey, value) => {
		setFilters((prev) => {
			const newFilters = { ...prev };

			if (value === undefined || value === null || value === '') {
				delete newFilters[filterKey];
			} else {
				newFilters[filterKey] = value;
			}

			return newFilters;
		});
		setPagination((prev) => ({
			...prev,
			current: 1, // Reset to first page when filter changes
		}));
	};

	const handleResetFilters = () => {
		setFilters({});
		setSearchValue('');
		setDateRange(null);
		setPagination((prev) => ({
			...prev,
			current: 1,
		}));
		setFilterDrawerOpen(false);
	};

	// Helper functions to check if filters are applied
	const hasStatusFilter = () => filters.is_active !== undefined && filters.is_active !== null;

	const hasApprovalStatusFilter = () =>
		filters.approval_status !== undefined && filters.approval_status !== null;

	const hasClientFilter = () => filters.client_id !== undefined && filters.client_id !== null;

	const hasProjectFilter = () => filters.project_id !== undefined && filters.project_id !== null;

	const hasVehicleTypeFilter = () =>
		filters.vehicle_type_id !== undefined && filters.vehicle_type_id !== null;

	const hasRouteFilter = () => filters.route_id !== undefined && filters.route_id !== null;

	const hasBillingTypeFilter = () =>
		filters.billing_type !== undefined && filters.billing_type !== null;

	const hasDateRangeFilter = () => dateRange && dateRange[0] && dateRange[1];

	const activeFiltersCount = [
		hasStatusFilter(),
		hasApprovalStatusFilter(),
		hasClientFilter(),
		hasProjectFilter(),
		hasVehicleTypeFilter(),
		hasRouteFilter(),
		hasBillingTypeFilter(),
		hasDateRangeFilter(),
	].filter(Boolean).length;

	// Handlers to remove individual filters
	const handleRemoveStatusFilter = () => {
		handleFilterChange('is_active', undefined);
	};

	const handleRemoveApprovalStatusFilter = () => {
		handleFilterChange('approval_status', undefined);
	};

	const handleRemoveClientFilter = () => {
		handleFilterChange('client_id', undefined);
	};

	const handleRemoveProjectFilter = () => {
		handleFilterChange('project_id', undefined);
	};

	const handleRemoveVehicleTypeFilter = () => {
		handleFilterChange('vehicle_type_id', undefined);
	};

	const handleRemoveRouteFilter = () => {
		handleFilterChange('route_id', undefined);
	};

	const handleRemoveBillingTypeFilter = () => {
		handleFilterChange('billing_type', undefined);
	};

	const handleRemoveDateRangeFilter = () => {
		setDateRange(null);
		setFilters((prev) => {
			const newFilters = { ...prev };
			delete newFilters.created_from;
			delete newFilters.created_to;
			return newFilters;
		});
		setPagination((prev) => ({
			...prev,
			current: 1,
		}));
	};

	// Deactivate reason modal state — used when an approved contract is being
	// toggled from active → inactive (routes through the edit-flow approval).
	const [deactivateModal, setDeactivateModal] = useState({ open: false, record: null });
	const [deactivateReason, setDeactivateReason] = useState('');
	const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
	const [collisionModal, setCollisionModal] = useState({
		open: false,
		message: '',
		conflict: null,
	});

	// Handle status toggle
	const handleStatusToggle = async (record) => {
		const contractId = record?.id;
		const currentStatus = record?.is_active;
		const isApproved = record?.approval_status === 'approved';
		const newStatus = !currentStatus;

		// Approved + active → inactive: gather reason and submit via edit_flow.
		if (isApproved && currentStatus === true) {
			setDeactivateModal({ open: true, record });
			setDeactivateReason('');
			return;
		}

		// Set loading state for this specific contract
		setStatusUpdateLoading((prev) => ({ ...prev, [contractId]: true }));

		try {
			await dispatch(
				updateClientContractV2({
					req_data: {
						id: contractId,
						is_active: newStatus,
					},
					setErrors: () => {},
				})
			);

			// Optimistically update local state
			setContractsData((prevData) =>
				prevData.map((contract) =>
					contract.id === contractId ? { ...contract, is_active: newStatus } : contract
				)
			);

			// Refresh data to ensure consistency
			await fetchClientContracts();
		} catch (error) {
			const serverError = error?.response?.data?.error;
			if (serverError?.code === 'ACTIVATION_BLOCKED_BY_COLLISION') {
				setCollisionModal({
					open: true,
					message: serverError?.message || '',
					conflict: serverError?.conflicting_contract || null,
				});
			} else {
				console.error('Error updating contract status:', error);
			}
			// Revert optimistic update on error by refreshing
			await fetchClientContracts();
		} finally {
			setStatusUpdateLoading((prev) => ({ ...prev, [contractId]: false }));
		}
	};

	const handleCollisionClose = () =>
		setCollisionModal({ open: false, message: '', conflict: null });

	const handleViewConflicting = () => {
		const { conflict } = collisionModal;
		if (!conflict?.id) return;
		handleCollisionClose();
		navigate(`/client-contracts/${conflict.id}`, {
			state: { clientId: conflict?.client_id },
		});
	};

	const handleDeactivateCancel = () => {
		if (deactivateSubmitting) return;
		setDeactivateModal({ open: false, record: null });
		setDeactivateReason('');
	};

	const handleDeactivateConfirm = async () => {
		const reason = (deactivateReason || '').trim();
		if (!reason) {
			message.error('Reason is required');
			return;
		}
		const target = deactivateModal.record;
		if (!target?.id) return;

		setDeactivateSubmitting(true);
		try {
			const response = await dispatch(
				updateClientContractV2({
					req_data: { id: target.id },
					edit_flow: true,
					comments: reason,
					changes: { is_active: false },
					setErrors: () => {},
				})
			);
			if (response?.status === 'success' || response?.data) {
				message.success(response?.message || 'Deactivation submitted for approval');
				setDeactivateModal({ open: false, record: null });
				setDeactivateReason('');
				await fetchClientContracts();
			} else {
				message.error(response?.message || 'Failed to submit deactivation');
			}
		} catch (error) {
			const errMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Failed to submit deactivation';
			message.error(errMsg);
		} finally {
			setDeactivateSubmitting(false);
		}
	};

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={2} mb={2}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<StyledFilterButton onClick={() => setFilterDrawerOpen(true)}>
						FILTERS
					</StyledFilterButton>
					<FilterDisplayBox>
						{hasStatusFilter() && (
							<FilterTag onClose={handleRemoveStatusFilter}>Status</FilterTag>
						)}
						{hasApprovalStatusFilter() && (
							<FilterTag onClose={handleRemoveApprovalStatusFilter}>
								Approval Status
							</FilterTag>
						)}
						{hasClientFilter() && (
							<FilterTag onClose={handleRemoveClientFilter}>Client</FilterTag>
						)}
						{hasProjectFilter() && (
							<FilterTag onClose={handleRemoveProjectFilter}>Project</FilterTag>
						)}
						{hasVehicleTypeFilter() && (
							<FilterTag onClose={handleRemoveVehicleTypeFilter}>
								Vehicle Type
							</FilterTag>
						)}
						{hasRouteFilter() && (
							<FilterTag onClose={handleRemoveRouteFilter}>Route</FilterTag>
						)}
						{hasDateRangeFilter() && (
							<FilterTag onClose={handleRemoveDateRangeFilter}>Created At</FilterTag>
						)}
						{hasBillingTypeFilter() && (
							<FilterTag onClose={handleRemoveBillingTypeFilter}>
								Billing Type
							</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleResetFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>

			<Box mt={3}>
				<Spin spinning={loading}>
					<AntdTable
						rowKey="id"
						columns={getTableColumns(navigate, handleStatusToggle, statusUpdateLoading)}
						dataSource={contractsData}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pageSize,
							total: pagination.total,
							showSizeChanger: true,
							pageSizeOptions: ['10', '20', '50', '100'],
							showTotal: (total) => `Total ${total} contracts`,
						}}
						onChange={handleTableChange}
					/>
				</Spin>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setFilterDrawerOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
			>
				<Box>
					{/* Status Filter */}
					<Box mb="2rem">
						<Text mb="5px">Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_active"
							value={filters.is_active}
							onChange={(val) => handleFilterChange('is_active', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={StatusOptions}
						/>
					</Box>

					{/* Approval Status Filter */}
					<Box mb="2rem">
						<Text mb="5px">Approval Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="approval_status"
							value={filters.approval_status}
							onChange={(val) => handleFilterChange('approval_status', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={ApprovalStatusOptions}
						/>
					</Box>

					{/* Client Filter */}
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_id"
							value={filters.client_id}
							onChange={(val) => handleFilterChange('client_id', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={filterOptions.clients}
						/>
					</Box>

					{/* Project Filter */}
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_id"
							value={filters.project_id}
							onChange={(val) => handleFilterChange('project_id', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={filterOptions.projects}
						/>
					</Box>

					{/* Billing Type Filter */}
					<Box mb="2rem">
						<Text mb="5px">Vehicle Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vehicle_type_id"
							value={filters.vehicle_type_id}
							onChange={(val) => handleFilterChange('vehicle_type_id', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select Vehicle Type"
							options={filterOptions.vehicleTypes}
						/>
					</Box>

					{/* Route Filter */}
					<Box mb="2rem">
						<Text mb="5px">Route</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="route_id"
							value={filters.route_id}
							onChange={(val) => handleFilterChange('route_id', val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select Route"
							options={filterOptions.routes}
						/>
					</Box>

					{/* Billing Type Filter */}
					<Box mb="2rem">
						<Text mb="5px">Billing Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="billing_type"
							value={filters.billing_type}
							onChange={(val) => handleFilterChange('billing_type', val)}
							allowClear
							placeholder="Select Billing Type"
							options={[
								{ value: 'Fixed', label: 'Fixed' },
								{ value: 'Variable', label: 'Variable' },
								{ value: 'Fixed + Variable', label: 'Fixed + Variable' },
							]}
						/>
					</Box>

					{/* Created At Date Range Filter */}
					<Box mb="2rem">
						<Text mb="5px">Created At</Text>
						<RangePicker
							style={{ width: '100%' }}
							placeholder={['Start Date', 'End Date']}
							value={dateRange}
							format="DD/MM/YYYY"
							onChange={(dates) => {
								setDateRange(dates);
								if (dates && dates[0] && dates[1]) {
									setFilters((prev) => ({
										...prev,
										created_from: moment(dates[0]).startOf('day').toISOString(),
										created_to: moment(dates[1]).endOf('day').toISOString(),
									}));
									setPagination((prev) => ({
										...prev,
										current: 1,
									}));
								} else {
									setFilters((prev) => {
										const newFilters = { ...prev };
										delete newFilters.created_from;
										delete newFilters.created_to;
										return newFilters;
									});
								}
							}}
						/>
					</Box>

					{/* Reset Filters Button */}
					<Box>
						<ButtonWrapper onClick={handleResetFilters} style={{ width: '100%' }}>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>

			<Modal
				title="Deactivate Contract"
				open={deactivateModal.open}
				onCancel={handleDeactivateCancel}
				onOk={handleDeactivateConfirm}
				okText="Submit"
				cancelText="Cancel"
				confirmLoading={deactivateSubmitting}
				maskClosable={!deactivateSubmitting}
				closable={!deactivateSubmitting}
			>
				<p style={{ marginBottom: 8 }}>
					Deactivating an approved contract requires approval. Please enter a reason.
				</p>
				<p style={{ marginBottom: 8, fontWeight: 500 }}>
					Reason <span style={{ color: '#ff4d4f' }}>*</span>
				</p>
				<Input.TextArea
					rows={4}
					value={deactivateReason}
					onChange={(e) => setDeactivateReason(e.target.value)}
					placeholder="Why is this contract being deactivated?"
					maxLength={500}
				/>
			</Modal>

			<Modal
				title="Cannot activate contract"
				open={collisionModal.open}
				onCancel={handleCollisionClose}
				footer={[
					<Button key="cancel" onClick={handleCollisionClose}>
						Cancel
					</Button>,
					collisionModal.conflict?.id && (
						<Button key="view" type="primary" onClick={handleViewConflicting}>
							View {collisionModal.conflict.id}
						</Button>
					),
				]}
			>
				<p style={{ marginBottom: 12 }}>
					{collisionModal.message ||
						'Another active contract overlaps with this one. Deactivate or expire the conflicting contract first.'}
				</p>
				{collisionModal.conflict && (
					<div
						style={{
							background: '#fafafa',
							border: '1px solid #f0f0f0',
							borderRadius: 6,
							padding: 12,
						}}
					>
						<p style={{ marginBottom: 8, fontWeight: 600 }}>
							Conflicting contract: {collisionModal.conflict.id}
						</p>
						{CONFLICT_FIELDS.map(({ key, label }) =>
							collisionModal.conflict?.[key] ? (
								<p key={key} style={{ marginBottom: 4 }}>
									<span style={{ color: '#8c8c8c' }}>{label}:</span>{' '}
									<span style={{ fontWeight: 500 }}>
										{collisionModal.conflict[key]}
									</span>
								</p>
							) : null
						)}
					</div>
				)}
			</Modal>
		</Box>
	);
}
